import {
  currentWorkspaceSchema,
  fieldWorkspaceCommandSchema,
  fieldWorkspaceJobSchema,
  fieldWorkspaceStatusSchema,
  fieldWorkspaceLaunchResultSchema,
  workspaceOperationSchema,
  workspacePauseCommandSchema,
} from '@mio/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef } from 'react';

import { ApiRequestError } from '@/shared/api/api-error';
import { apiRequest } from '@/shared/api/client';
import { workspaceQuery } from '@/shared/api/queries/workspace';
import { waitForWorkspaceOperation } from '@/shared/api/wait-for-workspace-operation';
import { editorSessionFor, workshopEditorContextKey } from '@/shared/workspace/editor-sessions';

import { beginFieldEditorSessionRecovery, fieldEditorSessionFor } from './field-workspace-session';

import type {
  FieldCapturePr,
  FieldSnapshot,
  FieldWorkspaceCommand,
  FieldWorkspaceStatus,
} from '@mio/shared';
const key = ['field-workspace'] as const;
export type FieldPrCaptureInput = Pick<
  FieldCapturePr,
  'title' | 'description' | 'ticketIds' | 'prId' | 'reviewerIds'
>;
export const FIELD_SWITCH_MUTATION_KEY = ['field-workspace', 'switch'] as const;
export function useFieldWorkspace(visible: boolean, switching = false) {
  return useQuery({
    queryKey: key,
    queryFn: ({ signal }) => apiRequest('/field/workspace', fieldWorkspaceStatusSchema, { signal }),
    enabled: visible,
    refetchInterval: (query) =>
      switching ||
      (query.state.data?.operation &&
        ['pending', 'running'].includes(query.state.data.operation.status))
        ? 1500
        : visible && query.state.data?.state === 'ready'
          ? 10000
          : false,
  });
}
export function useFieldWorkspaceCommand(state: FieldWorkspaceStatus | undefined) {
  const client = useQueryClient();
  const pending = useRef<FieldWorkspaceCommand | null>(null);
  return useMutation({
    mutationFn: async (action: FieldWorkspaceCommand['action']) => {
      if (!state?.workspaceId) throw new Error('Refresh your workspace first.');
      let editorSessionId: string | null = null;
      if (!pending.current && action !== 'activate') {
        if (!state.context) throw new Error('Start the Workbench workspace first.');
        editorSessionId = await fieldEditorSessionFor(state.context);
      }
      const command =
        pending.current ??
        fieldWorkspaceCommandSchema.parse({
          requestId: crypto.randomUUID(),
          action,
          expectedGeneration: state.generation,
          editorSessionId,
        });
      if (command.action !== action)
        throw new Error('Retry the previous workspace action before starting another.');
      pending.current = command;
      return apiRequest('/field/workspace/commands', fieldWorkspaceJobSchema, {
        method: 'POST',
        body: command,
      });
    },
    onSuccess: () => {
      pending.current = null;
      void client.invalidateQueries({ queryKey: key });
    },
    onError: (error) => {
      if (error instanceof ApiRequestError && error.status < 500) pending.current = null;
      void client.invalidateQueries({ queryKey: key });
    },
  });
}
/** Editor-session outcomes that mean "this tab's session cannot certify the Workshop buffers". */
export const EDITOR_SESSION_PAUSE_FAILURES = new Set([
  'editor_disconnected',
  'editor_context_changed',
  'editor_session_changed',
  'editor_in_use',
  'editor_unavailable',
  'editor_capture_interrupted',
]);
/** Compute states that settle on their own within a couple of minutes. */
const TRANSIENT_WORKSHOP_STATES = new Set([
  'queued',
  'requested',
  'provisioning',
  'starting',
  'stopping',
]);
const SETTLE_WAIT_MS = 3 * 60_000;
/**
 * A disk pause is refused while another browser still holds the Workshop editor. Once that
 * browser is gone, the runtime proves its absence within about two minutes; keep trying
 * through that window rather than sending the learner away to retry by hand.
 */
const ABSENCE_RETRY_MS = 20_000;
const ABSENCE_WAIT_MS = 4 * 60_000;
const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/**
 * Field shares the learner's one coding environment with Workshop. Switching pauses the
 * Workshop first, then starts Field on the same running compute: the pause checkpoints the
 * Workshop files and releases its activity without stopping the environment, so Field opens
 * in seconds instead of waiting for a provider stop and start. The Workshop page stays
 * mounted during app navigation, so its editor is usually still connected and the pause
 * flushes unsaved buffers through that session. When this tab holds no usable session (a new
 * tab or browser), the files already saved on disk are checkpointed instead; the server
 * refuses that only while an editor is still open elsewhere. Failed or paused Workshop
 * compute has nothing to save, so Field starts straight away.
 */
export function useFieldWorkspaceSwitch() {
  const client = useQueryClient();
  return useMutation({
    mutationKey: FIELD_SWITCH_MUTATION_KEY,
    mutationFn: async () => {
      let current = await apiRequest('/workspaces/me', currentWorkspaceSchema);
      // A Workshop that is still starting or pausing settles on its own; wait for it
      // rather than sending the learner away to try again later.
      const settleBy = Date.now() + SETTLE_WAIT_MS;
      while (current.workspace && TRANSIENT_WORKSHOP_STATES.has(current.workspace.state)) {
        if (Date.now() > settleBy)
          throw new Error('Your Workshop is still starting or pausing. Try again in a minute.');
        await sleep(2500);
        current = await apiRequest('/workspaces/me', currentWorkspaceSchema);
      }
      const workshop = current.workspace;
      if (workshop?.activeAttemptId && !['stopped', 'failed'].includes(workshop.state)) {
        if (workshop.state !== 'ready')
          throw new Error('Wait for your Workshop to finish starting or stopping, then try again.');
        const pause = async (editorSessionId: string | null) =>
          waitForWorkspaceOperation(
            await apiRequest('/workspaces/me/stop', workspaceOperationSchema, {
              method: 'POST',
              body: workspacePauseCommandSchema.parse({
                idempotencyKey: crypto.randomUUID(),
                expectedGeneration: workshop.generation,
                expectedAttemptId: workshop.activeAttemptId,
                editorSessionId,
                keepCompute: true,
                ...(editorSessionId ? {} : { saveMode: 'disk' }),
              }),
            }),
            { client },
          );
        let stopped = await pause(await editorSessionFor(workshopEditorContextKey(workshop)));
        if (
          stopped.status === 'failed' &&
          stopped.errorCode &&
          EDITOR_SESSION_PAUSE_FAILURES.has(stopped.errorCode)
        )
          stopped = await pause(null);
        const absenceBy = Date.now() + ABSENCE_WAIT_MS;
        while (
          stopped.status === 'failed' &&
          stopped.errorCode === 'editor_changed' &&
          Date.now() < absenceBy
        ) {
          await sleep(ABSENCE_RETRY_MS);
          stopped = await pause(null);
        }
        if (stopped.status !== 'succeeded')
          throw new ApiRequestError(
            409,
            stopped.errorCode ?? 'workspace_stop_failed',
            'Your Workshop could not be paused.',
          );
        await client.invalidateQueries({ queryKey: workspaceQuery.queryKey });
      }
      const status = await apiRequest('/field/workspace', fieldWorkspaceStatusSchema);
      if (!status.workspaceId) throw new Error('Refresh your workspace first.');
      if (status.selection === 'field' && status.state === 'ready') return null;
      return apiRequest('/field/workspace/commands', fieldWorkspaceJobSchema, {
        method: 'POST',
        body: fieldWorkspaceCommandSchema.parse({
          requestId: crypto.randomUUID(),
          action: 'activate',
          expectedGeneration: status.generation,
          editorSessionId: null,
        }),
      });
    },
    onSettled: () => {
      void client.invalidateQueries({ queryKey: key });
      void client.invalidateQueries({ queryKey: workspaceQuery.queryKey });
    },
  });
}
export function useFieldWorkspaceLaunch(state: FieldWorkspaceStatus | undefined) {
  return useMutation({
    mutationFn: async (recoverDisconnected: boolean) => {
      if (!state?.context || !state.workspaceId)
        throw new Error('Start the Workbench workspace first.');
      const context = state.context;
      const recovery = recoverDisconnected ? await beginFieldEditorSessionRecovery(context) : null;
      const launch = await apiRequest('/field/workspace/launch', fieldWorkspaceLaunchResultSchema, {
        method: 'POST',
        body: {
          expected: context,
          sessionId: recovery?.sessionId ?? (await fieldEditorSessionFor(context)),
          ...(recoverDisconnected ? { recoverDisconnected: true } : {}),
        },
      });
      recovery?.commit();
      return { context, launch };
    },
  });
}

export function useFieldPrCapture(revision: number | undefined) {
  const client = useQueryClient();
  const pending = useRef<FieldCapturePr | null>(null);
  return useMutation({
    mutationFn: async (input: FieldPrCaptureInput) => {
      if (revision === undefined) throw new Error('Refresh Workbench before capturing.');
      if (!pending.current) {
        const workspace = await apiRequest('/field/workspace', fieldWorkspaceStatusSchema);
        if (
          !workspace.enabled ||
          workspace.selection !== 'field' ||
          !workspace.context ||
          workspace.state !== 'ready' ||
          !workspace.workspaceId
        )
          throw new Error('Start and open the native Workbench IDE before capturing its source.');
        if (workspace.operation && ['pending', 'running'].includes(workspace.operation.status))
          throw new Error('Wait for the workspace action to finish before capturing your code.');
        const requestId = crypto.randomUUID();
        pending.current = {
          ...input,
          requestId,
          expectedRevision: revision,
          capture: {
            operationId: requestId,
            issuedAt: new Date().toISOString(),
            expected: workspace.context,
            editorSessionId: await fieldEditorSessionFor(workspace.context),
          },
        };
      } else if (
        JSON.stringify([
          pending.current.prId,
          pending.current.title,
          pending.current.description,
          pending.current.ticketIds,
          pending.current.reviewerIds,
        ]) !==
        JSON.stringify([
          input.prId,
          input.title,
          input.description,
          input.ticketIds,
          input.reviewerIds,
        ])
      )
        throw new Error('Retry the same capture before changing its pull request or description.');
      const { fieldCapturePrSchema, fieldSnapshotSchema } = await import('@mio/shared');
      return apiRequest('/field/pr-captures', fieldSnapshotSchema, {
        method: 'POST',
        body: fieldCapturePrSchema.parse(pending.current),
      });
    },
    onSuccess: (next) => {
      pending.current = null;
      client.setQueryData<FieldSnapshot>(['field', 'current'], (current) =>
        current?.engagement.id === next.engagement.id &&
        current.engagement.revision > next.engagement.revision
          ? current
          : next,
      );
      void client.invalidateQueries({ queryKey: key });
    },
    onError: (error) => {
      if (error instanceof ApiRequestError && error.status < 500) pending.current = null;
      void client.invalidateQueries({ queryKey: ['field', 'current'] });
    },
  });
}
